import { FormRadio, FormTextArea } from "components/form-fields";
import { GridLayout } from "components/grid";
import { ShowHide } from "components/layout";
import { NotificationInline } from "components/notifications";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { useEventWizard } from "pages/asset/events/shared/EventWizardContext";
import React, { useEffect, useState, Fragment } from "react";
import { EventAddLaterOptions } from 'pages/asset/events/modals/DefaultModels';

export function CommentFailEvent({}) {

    const { 
        event,
        eventType, 
        eventUpdateRequest, 
        saving, 
        loadingData, 
        stepsConfig, 
        setStepIsValid, 
        onEventUpdateRequestChange, 
        setStepsConfig,
        setActiveStep,
        setCanSave
    } = useEventWizard();

    const [originalStepData, setOriginalStepData] = useState({ comment: "", failed: false });
    const [stepData, setStepData] = useState(null);

    const isStepDataDirty = useDirtyData(originalStepData, stepData);

    const [isValid, errors, validate] = useValidation();

    const config = stepsConfig.CommentFailEvent;
    const { fields, addCommentLater } = config;

    const isDisabled = saving || loadingData;
    const toShowFailComment = eventType?.includeComment || eventType?.requiresComment || (stepData?.failed && eventType?.requiresCommentFailedOnly);

    useEffect(() => {
        setActiveStep("CommentFail");
    }, []);

    useEffect(() => {
        // if the details we care about haven't changed, then do nothing.
        if (event?.comment === originalStepData?.comment && event?.failed === originalStepData?.failed) {
            return;
        }

        setOriginalStepData({
            comment: event?.comment,
            failed: event?.failed
        });
    }, [event]);

    const handleHasFailedChange = (e) => {
        const failed = e.target.value === "true";
        setStepData(prev => ({
            ...prev,
            failed: failed
        }));
    }

    const handleCommentChange = (e) => {
        const comment = e.target.value;
        setStepData(prev => ({
            ...prev,
            comment
        }));
    };

    function onRadioFieldChange(item) {
        setStepsConfig(prev => ({
            ...prev,
            CommentFailEvent: {
                ...prev.CommentFailEvent,
                addCommentLater: item.currentTarget.value === "true"
            }
        }));
    }

    useEffect(() => {
        if (!stepData) {
            return;
        }

        const isCommentRequired =
            config.commentIsRequired &&
            !config.addCommentLater &&
            (
                !eventType?.requiresCommentFailedOnly ||
                (eventType?.requiresCommentFailedOnly && stepData.failed)
            );

        const rules = [
            {
                fieldName: "comment",
                required: isCommentRequired,
            }
        ];

        validate(rules, stepData);
    }, [isStepDataDirty, stepData, config, eventType]);

    useEffect(() => {
        setStepIsValid(isValid)
        setCanSave(isStepDataDirty && isValid);
        isValid && stepData && onEventUpdateRequestChange(stepData);
    }, [isValid, stepData, isStepDataDirty]);

    useEffect(() => {
        if (stepData !== null) {
            return;
        }

        setStepData({
            comment: eventUpdateRequest.comment,
            failed: eventUpdateRequest.failed
        });
    }, [eventUpdateRequest]);

    return (
        <div>
            <h2>{toShowFailComment ? "Event Comment" : "Event Failure"}</h2>

            <NotificationInline
                id="notification-event-helper"
                loadingData={loadingData}
                show={eventType?.eventHelperText && eventType.eventHelperText.length > 0}
                text={eventType.eventHelperText}
                allowClose={false} />

            <GridLayout numberOfColumns={2} gap={0}>

                <ShowHide
                    evaluator={fields.includes('hasEventFail')}
                    show={
                        <FormRadio
                            selectedValue={stepData?.failed}
                            onChange={handleHasFailedChange}
                            headingText="Has this event failed?"
                            disabled={isDisabled}
                            loading={loadingData}
                        />
                    }
                />

                <ShowHide
                    evaluator={toShowFailComment}
                    show={
                        <Fragment>
                            <ShowHide
                                evaluator={config.commentIsRequired}
                                show={
                                    <FormRadio
                                        disabled={isDisabled || !!stepData?.comment}
                                        options={EventAddLaterOptions}
                                        onChange={(item) => onRadioFieldChange(item)}
                                        headingText="When do you want to add a comment?"
                                        selectedValue={addCommentLater}
                                        loading={loadingData}
                                        id="input-add-comment-later"
                                    />
                                }
                            />

                            <ShowHide
                                evaluator={!addCommentLater}
                                show={(
                                    <FormTextArea
                                        headingText="Comment"
                                        helperText={eventType?.commentHelperText}
                                        dangerHelperText={errors.comment}
                                        value={stepData?.comment}
                                        onChange={handleCommentChange}
                                        disabled={isDisabled}
                                    />
                                )}
                            />
                        </Fragment>
                    }
                />

            </GridLayout>
        </div>
    );
}

CommentFailEvent.propTypes = {
};